import { useState } from "react";

/** Formats accepted by customer portal browser upload (keep in sync with CustomerSlideUpload). */
export const PORTAL_SLIDE_FORMATS = [
  {
    ext: ".svs",
    name: "Aperio SVS",
    detail: "Single-file whole-slide image from Aperio / Leica scanners.",
  },
  {
    ext: ".ndpi",
    name: "Hamamatsu NDPI",
    detail: "Single-file whole-slide image from Hamamatsu scanners.",
  },
  {
    ext: ".tif / .tiff",
    name: "TIFF",
    detail:
      "TIFF whole-slide or large raster images accepted by the processing pipeline.",
  },
  {
    ext: ".med",
    name: "AIxMed MED",
    detail: "AIxMed intermediate / packaged slide format.",
  },
  {
    ext: ".zip",
    name: "ZIP archive",
    detail:
      "A ZIP of a MIRAX (MRXS) slide package. Browser upload does not accept raw MRXS folders—zip the package first, or use Smart Agent which can zip MRXS directories automatically.",
  },
];

/**
 * Link + modal listing supported slide image formats for portal upload.
 */
export default function SlideFormatHelp({
  linkLabel = "Supported formats",
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button type="button" style={styles.link} onClick={() => setOpen(true)}>
        {linkLabel}
      </button>
      {open ? (
        <div
          style={styles.backdrop}
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="slide-format-help-title"
          >
            <div style={styles.header}>
              <h2 id="slide-format-help-title" style={{ margin: 0, fontSize: 18 }}>
                Supported slide formats
              </h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close">
                Close
              </button>
            </div>
            <p style={styles.intro}>
              These extensions are accepted for <strong>browser upload</strong> on
              this page. File names must end with one of the extensions below
              (case-insensitive). Subfolders are searched when you use{" "}
              <strong>Choose folder</strong>.
            </p>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Extension</th>
                  <th style={styles.th}>Format</th>
                  <th style={styles.th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {PORTAL_SLIDE_FORMATS.map((f) => (
                  <tr key={f.ext}>
                    <td style={styles.td}>
                      <code>{f.ext}</code>
                    </td>
                    <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{f.name}</td>
                    <td style={styles.td}>{f.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={styles.footer}>
              <strong>Not supported here:</strong> unpackaged MIRAX folders
              (directory named <code>.mrxs</code> with companion data files). Use a{" "}
              <code>.zip</code> of that package, or Smart Agent (Organization →
              Software) for folder watch and automatic MRXS packaging.
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}

const styles = {
  link: {
    background: "none",
    border: "none",
    padding: 0,
    color: "#2563eb",
    fontSize: "inherit",
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "underline",
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    width: "min(720px, 100%)",
    maxHeight: "85vh",
    overflow: "auto",
    background: "#fff",
    borderRadius: 8,
    padding: 20,
    boxShadow: "0 16px 40px rgba(0,0,0,0.2)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  intro: {
    fontSize: 13,
    color: "#4b5563",
    margin: "0 0 14px",
    lineHeight: 1.45,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    marginBottom: 14,
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #e5e7eb",
    background: "#f9fafb",
    fontSize: 12,
    color: "#6b7280",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "10px",
    borderBottom: "1px solid #eee",
    verticalAlign: "top",
    lineHeight: 1.4,
    color: "#111827",
  },
  footer: {
    fontSize: 13,
    color: "#4b5563",
    margin: 0,
    lineHeight: 1.45,
  },
};
